import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "@tanstack/react-router";
import { ArrowUpRight, Check, ClockStopwatch, Copy01, Phone01, RefreshCcw01, SlashCircle01, Tag01, Trash01, XClose } from "@untitledui/icons";
import { Table } from "@/components/application/table/table";
import { Badge } from "@/components/base/badges/badges";
import { Button } from "@/components/base/buttons/button";
import { Checkbox } from "@/components/base/checkbox/checkbox";
import { LoadingIndicator } from "@/components/application/loading-indicator/loading-indicator";
import { useConnectors } from "@/lib/connectors";
import { usePaatProfilesList } from "@/lib/paatProfiles";
import { formatDateTime } from "@/lib/format";
import { useDebounce } from "@/hooks/use-debounce";
import { useClipboard } from "@/hooks/use-clipboard";
import { AppSelect } from "@/components/app/AppSelect";
import { IconAction } from "@/components/app/IconAction";
import {
  useTokens,
  useSyncTokenState,
  tokenStateColor,
  tokenActionBlockKey,
  TOKEN_STATES,
  type TokenSort,
  type TokenSortColumn,
  type TokenSummary,
  type TokenActionBlock,
} from "@/lib/tokens";
import { Pager } from "@/components/app/Pager";
import { InlineAlert } from "@/components/application/alerts/inline-alert";
import {
  tokenInputCls as inputCls,
  apiErrText,
  DeactivateDialog,
  DeleteDialog,
  TagsDialog,
  ExpirationDialog,
  PaatDialog,
} from "@/pages/tokens/TokenActionDialogs";
import { BulkToolbar } from "@/pages/tokens/BulkToolbar";
import {
  BulkDeactivateDialog,
  BulkDeleteDialog,
  BulkExpirationDialog,
  BulkTagsDialog,
  type BulkScope,
} from "@/pages/tokens/BulkActionDialogs";
import {
  EMPTY_SELECTION,
  isSelected,
  pageSelectionState,
  selectAllMatching,
  selectionCount,
  togglePage,
  toggleRow,
  BulkTokenAction,
  type TokenSelection,
} from "@/lib/bulkTokens";

/** Nabídka velikostí stránky; 50 je kompromis mezi „vidím dost" a „nečekám na render". */
const PAGE_SIZES = [20, 50, 100, 200] as const;
const DEFAULT_PAGE_SIZE = 50;

export function TokensPage() {
  const { t } = useTranslation();

  const [state, setState] = useState("");
  const [authServerType, setAuthServerType] = useState("");
  const [paatProfileId, setPaatProfileId] = useState("");
  const [tokenIdInput, setTokenIdInput] = useState("");
  const [usernameInput, setUsernameInput] = useState("");
  const [tagInput, setTagInput] = useState("");
  const tokenId = useDebounce(tokenIdInput, 300);
  const username = useDebounce(usernameInput, 300);
  const tag = useDebounce(tagInput, 300);

  const connectors = useConnectors();
  const paatProfiles = usePaatProfilesList();
  const { copy, copied } = useClipboard();

  // Výchozí řazení: nejnovější tokeny nahoře. Bez explicitního řazení přicházelo
  // pořadí z databáze (nedeterministické), takže „poslední aktivace“ se hledala očima.
  const [sort, setSort] = useState<TokenSort>({ column: "createdAt", direction: "descending" });
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const filter = useMemo(
    () => ({ state, authServerType, tokenId, username, tag, paatProfileId }),
    [state, authServerType, tokenId, username, tag, paatProfileId],
  );
  const { data, isLoading, isError, isFetching } = useTokens(page, pageSize, sort, filter);
  const rows = data?.content ?? [];

  // Každá změna filtru, řazení i velikosti stránky vrací na první stránku —
  // jinak by uživatel po zúžení výsledků skončil na stránce mimo rozsah (prázdná tabulka).
  const changeSort = (next: TokenSort) => {
    setSort(next);
    setPage(0);
  };
  const changeFilter = (apply: () => void) => {
    apply();
    setPage(0);
  };

  // Výběr pro hromadné akce. Reset visí na `filter` (memo nad debounced hodnotami),
  // ne na `changeFilter` — ten se volá i při změně velikosti stránky, která množinu
  // výsledků nemění. Změna řazení ani stránky výběr neruší; přenos výběru mezi
  // stránkami je celý jeho smysl.
  const [selection, setSelection] = useState<TokenSelection>(EMPTY_SELECTION);
  useEffect(() => setSelection(EMPTY_SELECTION), [filter]);

  const pageIds = useMemo(() => rows.map((r) => r.tokenId), [rows]);
  const pageSel = pageSelectionState(selection, pageIds);
  const totalElements = data?.totalElements ?? 0;
  const selectedCount = selectionCount(selection, totalElements);
  const [bulkAction, setBulkAction] = useState<BulkTokenAction | null>(null);

  /**
   * Kolik z *viditelných* vybraných řádků má akci zakázanou. O neviditelné části výběru
   * se nedá tvrdit nic — tu server přeskočí a započítá do `skipped`.
   */
  const blockedVisible = (action: BulkTokenAction) =>
    rows.filter((r) => isSelected(selection, r.tokenId) && r.actions[action] != null).length;

  const bulkScope = (action: BulkTokenAction): BulkScope => ({
    count: selectedCount,
    selection,
    filter,
    blockedVisible: blockedVisible(action),
  });

  const finishBulk = () => {
    setSelection(EMPTY_SELECTION);
    setBulkAction(null);
  };

  // Modal state — jedna akce nad jedním tokenem.
  const [deactivateT, setDeactivateT] = useState<TokenSummary | null>(null);
  const [deleteT, setDeleteT] = useState<TokenSummary | null>(null);
  const [tagsT, setTagsT] = useState<TokenSummary | null>(null);
  const [expirationT, setExpirationT] = useState<TokenSummary | null>(null);
  const [paatT, setPaatT] = useState<TokenSummary | null>(null);
  const sync = useSyncTokenState();

  /** Kód důvodu → přeložená věta do tooltipu; `undefined` = akce povolena. */
  const blockReason = (reason: TokenActionBlock | null) => (reason ? t(tokenActionBlockKey(reason)) : undefined);

  const hasFilters = state || authServerType || paatProfileId || tokenIdInput || usernameInput || tagInput;
  const clearFilters = () =>
    changeFilter(() => {
      setState("");
      setAuthServerType("");
      setPaatProfileId("");
      setTokenIdInput("");
      setUsernameInput("");
      setTagInput("");
    });

  return (
    <div className="flex flex-col gap-6">
      <div>
        <h1 className="text-display-sm font-semibold text-primary">{t("tokens.title")}</h1>
        <p className="mt-1 text-md text-tertiary">{t("tokens.subtitle")}</p>
      </div>

      {/* Filtry */}
      <div className="flex flex-wrap items-end gap-3">
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("tokens.col.tokenId")}</span>
          <input
            className={inputCls}
            value={tokenIdInput}
            onChange={(e) => changeFilter(() => setTokenIdInput(e.target.value))}
            placeholder={t("tokens.filter.tokenIdPlaceholder")}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("tokens.col.state")}</span>
          <AppSelect value={state} onChange={(e) => changeFilter(() => setState(e.target.value))}>
            <option value="">{t("tokens.filter.all")}</option>
            {TOKEN_STATES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </AppSelect>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("tokens.col.connector")}</span>
          <AppSelect value={authServerType} onChange={(e) => changeFilter(() => setAuthServerType(e.target.value))}>
            <option value="">{t("tokens.filter.all")}</option>
            {connectors.data?.map((c) => (
              <option key={c.connectorType} value={c.connectorType}>
                {c.connectorType}
              </option>
            ))}
          </AppSelect>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("tokens.col.paat")}</span>
          <AppSelect value={paatProfileId} onChange={(e) => changeFilter(() => setPaatProfileId(e.target.value))}>
            <option value="">{t("tokens.filter.all")}</option>
            {paatProfiles.data?.map((p) => (
              <option key={p.id} value={p.id}>
                {p.id} ({p.platform})
              </option>
            ))}
          </AppSelect>
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("tokens.col.username")}</span>
          <input
            className={inputCls}
            value={usernameInput}
            onChange={(e) => changeFilter(() => setUsernameInput(e.target.value))}
            placeholder={t("tokens.filter.usernamePlaceholder")}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("tokens.col.tags")}</span>
          <input
            className={inputCls}
            value={tagInput}
            onChange={(e) => changeFilter(() => setTagInput(e.target.value))}
            placeholder={t("tokens.filter.tagPlaceholder")}
          />
        </label>
        <label className="flex flex-col gap-1">
          <span className="text-xs font-medium text-tertiary">{t("pager.pageSize")}</span>
          <AppSelect
            value={pageSize}
            onChange={(e) => changeFilter(() => setPageSize(Number(e.target.value)))}
          >
            {PAGE_SIZES.map((s) => (
              <option key={s} value={s}>
                {s}
              </option>
            ))}
          </AppSelect>
        </label>
        {hasFilters ? (
          <Button color="secondary" size="md" iconLeading={XClose} onPress={clearFilters}>
            {t("tokens.filter.clear")}
          </Button>
        ) : null}
      </div>

      {isLoading && <LoadingIndicator />}
      {isError && <p className="text-sm text-error-primary">{t("common.loadError")}</p>}

      {data && (
        <>
          {/* Výsledek synchronizace. Sync je jediná akce v řádku bez dialogu, takže bez
              tohohle proběhla tiše. Řádek se nezvýrazňuje — stačí Token ID v hlášce. */}
          {sync.isSuccess && sync.data && (
            <InlineAlert variant={sync.data.changed ? "success" : "info"} data-testid="sync-result">
              <span className="font-mono">{sync.variables}</span>{" "}
              {sync.data.changed
                ? t("tokens.sync.changed", { from: sync.data.oldState, to: sync.data.newState })
                : t("tokens.sync.unchanged", { state: sync.data.newState })}
            </InlineAlert>
          )}
          {sync.isError && (
            <InlineAlert variant="error" title={t("tokens.sync.failed")} data-testid="sync-result">
              <span className="font-mono">{sync.variables}</span> {apiErrText(sync.error)}
            </InlineAlert>
          )}

          <BulkToolbar
            total={data.totalElements}
            selectedCount={selectedCount}
            allMatching={selection.all}
            pageFullySelected={pageSel.isSelected}
            onSelectAllMatching={() => setSelection(selectAllMatching())}
            onClear={() => setSelection(EMPTY_SELECTION)}
            onAction={setBulkAction}
          />

          {rows.length === 0 ? (
            <div className="rounded-xl py-16 text-center ring-1 ring-secondary">
              <p className="text-sm text-tertiary">{t("tokens.results.empty")}</p>
            </div>
          ) : (
            <div className="overflow-x-auto rounded-xl ring-1 ring-secondary">
              <Table
                aria-label={t("tokens.title")}
                selectionMode="none"
                sortDescriptor={sort}
                onSortChange={(d) => changeSort({ column: d.column as TokenSortColumn, direction: d.direction })}
              >
                <Table.Header>
                  {/* Vlastní výběrový sloupec, NE `selectionMode="multiple"`. Vestavěný
                      výběr react-aria dělá z řádku stiskatelný prvek (`usePress`), který
                      při stisku nastaví `user-select: none` a klik kamkoli do řádku
                      přepne zaškrtnutí — text v buňkách by nešel označit ani zkopírovat.
                      Viz komentář u `Table.Row` níže, který popisuje týž problém u odkazů. */}
                  <Table.Head id="select" className="w-11 px-4 md:pl-6">
                    {/* `slot={null}` = nepřebírat kontext react-aria. Sloupec tabulky
                        poskytuje `CheckboxContext` **jen** se slotem `selection` (na rozdíl
                        od řádku, který má i výchozí slot), takže Checkbox bez slotu tam
                        skončí na „A slot prop is required". Výběr si řídíme sami. */}
                    <Checkbox
                      slot={null}
                      size="md"
                      aria-label={t("tokens.bulk.selectPage")}
                      isSelected={pageSel.isSelected}
                      isIndeterminate={pageSel.isIndeterminate}
                      onChange={(v) => setSelection((s) => togglePage(s, pageIds, v))}
                    />
                  </Table.Head>
                  <Table.Head isRowHeader id="tokenId" allowsSorting label={t("tokens.col.tokenId")} />
                  <Table.Head id="username" allowsSorting label={t("tokens.col.username")} />
                  <Table.Head id="state" allowsSorting label={t("tokens.col.state")} />
                  <Table.Head id="authServerType" allowsSorting label={t("tokens.col.connector")} />
                  <Table.Head id="paat" label={t("tokens.col.paat")} />
                  <Table.Head id="tags" label={t("tokens.col.tags")} />
                  <Table.Head id="createdAt" allowsSorting label={t("tokens.col.created")} />
                  <Table.Head id="activatedAt" allowsSorting label={t("tokens.field.activated")} />
                  <Table.Head id="expiresAt" allowsSorting label={t("tokens.field.expires")} />
                  <Table.Head id="actions" label={t("tokens.col.actions")} />
                </Table.Header>
                {/* `dependencies` invaliduje cache položek kolekce. Bez nich react-aria
                    řádky vyrobené z `items` + funkce jednou zapamatuje a po změně výběru
                    překreslí jen věci mimo kolekci (lišta s počtem) — zaškrtávátka pak
                    vypadají prázdná, přestože výběr sedí. */}
                <Table.Body items={rows} dependencies={[selection]}>
                  {(row) => {
                    const actions = row.actions;
                    const checked = isSelected(selection, row.tokenId);
                    return (
                    // Řádek záměrně NENÍ odkaz — react-aria by na něj nasadilo
                    // `user-select: none` a text v buňkách by nešel označit ani
                    // zkopírovat. Detail se otevírá odkazem na Token ID nebo akcí
                    // „Detail“ v poslední buňce.
                    // `[&:hover]` = obyčejný CSS hover. Vestavěné `hover:bg-secondary`
                    // v Table.Row se u prvků s `data-rac` váže na `data-hovered`, které
                    // react-aria u neinteraktivního řádku nenastavuje — bez tohohle by
                    // řádky po zrušení odkazu přestaly reagovat na najetí myší.
                    <Table.Row
                      id={row.tokenId}
                      className={checked ? "bg-secondary [&:hover]:bg-secondary" : "[&:hover]:bg-secondary"}
                    >
                      <Table.Cell className="w-11 px-4 py-4 md:pl-6">
                        <Checkbox
                          slot={null}
                          size="md"
                          aria-label={t("tokens.bulk.selectRow", { tokenId: row.tokenId })}
                          isSelected={checked}
                          onChange={() => setSelection((s) => toggleRow(s, row.tokenId))}
                        />
                      </Table.Cell>
                      <Table.Cell>
                        <span className="flex items-center gap-1">
                          <Link
                            to="/tokens/$tokenId"
                            params={{ tokenId: row.tokenId }}
                            className="font-medium text-brand-secondary hover:underline"
                          >
                            {row.tokenId}
                          </Link>
                          {/* Ikona je vidět trvale. Odkrývání přes `group-hover` tady
                              nefunguje: s `tailwindcss-react-aria-components` se hover
                              u prvků s `data-rac` čte z `data-hovered`, které react-aria
                              nastavuje jen na interaktivních řádcích — a řádek záměrně
                              interaktivní není. */}
                          <IconAction
                            icon={copied === row.tokenId ? Check : Copy01}
                            label={t("tokens.action.copyId")}
                            onPress={() => void copy(row.tokenId, row.tokenId)}
                          />
                        </span>
                      </Table.Cell>
                      <Table.Cell>{row.username}</Table.Cell>
                      <Table.Cell>
                        <Badge type="pill-color" size="sm" color={tokenStateColor(row.state)}>
                          {row.state}
                        </Badge>
                      </Table.Cell>
                      <Table.Cell>{row.authServerType}</Table.Cell>
                      <Table.Cell>
                        {row.paatProfileId ? (
                          <span className="text-tertiary">
                            {row.paatProfileId}
                            {row.paatProfilePlatform ? ` · ${row.paatProfilePlatform}` : ""}
                          </span>
                        ) : (
                          "—"
                        )}
                      </Table.Cell>
                      <Table.Cell>
                        {row.tags && row.tags.length > 0 ? (
                          <span className="flex flex-wrap gap-1">
                            {row.tags.map((tg) => (
                              <Badge key={tg} type="pill-color" size="sm" color="gray">
                                {tg}
                              </Badge>
                            ))}
                          </span>
                        ) : (
                          "—"
                        )}
                      </Table.Cell>
                      <Table.Cell>{formatDateTime(row.createdAt)}</Table.Cell>
                      <Table.Cell>{formatDateTime(row.activatedAt)}</Table.Cell>
                      <Table.Cell>{row.expiresAt ? formatDateTime(row.expiresAt) : "—"}</Table.Cell>
                      <Table.Cell>
                        {/* Detail první, destruktivní akce (deaktivace, smazání) poslední.
                            Dostupnost akcí musí být stejná jako v detailu → `tokenActionsFor`. */}
                        <div className="flex gap-0.5">
                          <IconAction icon={ArrowUpRight} label={t("tokens.action.detail")} href={`/tokens/${row.tokenId}`} />
                          <IconAction
                            icon={RefreshCcw01}
                            label={t("tokens.action.sync")}
                            isLoading={sync.isPending && sync.variables === row.tokenId}
                            disabledReason={blockReason(actions.SYNC)}
                            onPress={() => sync.mutate(row.tokenId)}
                          />
                          <IconAction
                            icon={Tag01}
                            label={t("tokens.action.tags")}
                            disabledReason={blockReason(actions.TAGS)}
                            onPress={() => setTagsT(row)}
                          />
                          <IconAction
                            icon={ClockStopwatch}
                            label={t("tokens.action.expiration")}
                            disabledReason={blockReason(actions.EXPIRATION)}
                            onPress={() => setExpirationT(row)}
                          />
                          <IconAction
                            icon={Phone01}
                            label={t("tokens.action.paat")}
                            disabledReason={blockReason(actions.PAAT)}
                            onPress={() => setPaatT(row)}
                          />
                          {!actions.DEACTIVATE && (
                            <IconAction icon={SlashCircle01} label={t("tokens.action.deactivate")} onPress={() => setDeactivateT(row)} />
                          )}
                          <IconAction icon={Trash01} label={t("tokens.action.delete")} onPress={() => setDeleteT(row)} />
                        </div>
                      </Table.Cell>
                    </Table.Row>
                    );
                  }}
                </Table.Body>
              </Table>
            </div>
          )}

          <Pager page={data} onPage={setPage} isFetching={isFetching} />
        </>
      )}

      {bulkAction === BulkTokenAction.EXPIRATION && (
        <BulkExpirationDialog scope={bulkScope(BulkTokenAction.EXPIRATION)} onClose={() => setBulkAction(null)} onFinished={finishBulk} />
      )}
      {bulkAction === BulkTokenAction.TAGS && (
        <BulkTagsDialog scope={bulkScope(BulkTokenAction.TAGS)} onClose={() => setBulkAction(null)} onFinished={finishBulk} />
      )}
      {bulkAction === BulkTokenAction.DEACTIVATE && (
        <BulkDeactivateDialog scope={bulkScope(BulkTokenAction.DEACTIVATE)} onClose={() => setBulkAction(null)} onFinished={finishBulk} />
      )}
      {bulkAction === BulkTokenAction.DELETE && (
        <BulkDeleteDialog scope={bulkScope(BulkTokenAction.DELETE)} onClose={() => setBulkAction(null)} onFinished={finishBulk} />
      )}

      {deactivateT && <DeactivateDialog token={deactivateT} onClose={() => setDeactivateT(null)} />}
      {deleteT && <DeleteDialog token={deleteT} onClose={() => setDeleteT(null)} />}
      {tagsT && <TagsDialog token={tagsT} onClose={() => setTagsT(null)} />}
      {expirationT && <ExpirationDialog token={expirationT} onClose={() => setExpirationT(null)} />}
      {paatT && <PaatDialog token={paatT} onClose={() => setPaatT(null)} />}
    </div>
  );
}
